import React, { useEffect, useMemo, useRef, useState } from 'react';
import FormFieldContext from 'components/forms/FormFieldContext';
import SecondaryButton from 'components/buttons/SecondaryButton';
import PrimaryButton from 'components/buttons/PrimaryButton';
import { BillingData, createPaymentMethod, PaymentMethod, PaymentMethodType } from '../BillingTypes';
import { useModal } from 'components/modals/ModalContext';
import { useGlobalContext } from 'GlobalContext';
import { CardElement, IbanElement, useElements, useStripe } from '@stripe/react-stripe-js';
import Dropdown from 'components/forms/basefields/Dropdown';
import { useTranslation } from 'react-i18next';
import { BillingDetailsRef } from '../subscription-wizard/BillingDetails';
import { FieldType } from 'types/FieldTypes';
import { generateFieldComponents } from 'components/forms/GenerateFieldComponents';
import { saveData } from 'services/api/saveData';
import { createStripeCustomer } from '../functions/createStripeCustomer';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { encryptData } from 'services/authentication/encryptData';
import '../../../../style/scss/billing.scss';
import { createSepaSetupIntent } from '../functions/createSepaSetupIntent';
import Checkbox from 'components/forms/basefields/Checkbox';

interface AddPaymentDetailsFormProps {
    viewKey?: string;
    subscriptionId: number | null;
    billingData: BillingData;
}

const AddPaymentDetailsForm: React.FC<AddPaymentDetailsFormProps> = ({ viewKey, subscriptionId, billingData }) => {
    const { t } = useTranslation();
    const authContext = useAuthContext();  
    const { activeFeatures } = useAuthContext();  
    const { setFloatingAlert, errorMessages, setErrorMessages } = useGlobalContext();
    const { revealModal, closeModal, modalStack } = useModal();
    const [editing, setEditing] = useState(true);
    const [updatedData, setUpdatedData] = useState<Record<string, any>>({});
    const [paymentMethodData, setPaymentMethodData] = useState<PaymentMethod>(createPaymentMethod);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>(paymentMethodData['payment_type'] || 'sepa_debit');
    const [buttonLoader, setButtonLoader] = useState(false);
    const billingDetailsRef = useRef<BillingDetailsRef>({ getCardElement: () => null, getIbanElement: () => null });
    const [isPaymentMethodEntered, setIsPaymentMethodEntered] = useState(false);
    const [sepaMandateAccepted, setSepaMandateAccepted] = useState(false);
    const [showSepaWarning, setShowSepaWarning] = useState(false);
    const [showValidationMessage, setShowValidationMessage] = useState(false);
    const topModal = modalStack[modalStack.length - 1];
    const stripe = useStripe();
    const elements = useElements();

    const [paymentMethods] = useState<{ value: string, name: string }[]>([ 
        { value: 'sepa_debit', name: 'billing.payment_method.sepa' }, 
    ]);

    const fields: FieldType[] = [
        {
            type: 'header',
            header: 'billing.general.company_details_header',
        },
        {
            type: 'text',
            name: 'company_name',
            label: 'billing.general.company_name_label',
            placeholder: 'billing.general.company_name_label',
        },
        {
            type: 'text',
            name: 'first_name',
            label: 'billing.general.first_name_label',
            placeholder: 'billing.general.first_name_label',
            width: '50%',
            group: 'field-row',
        },
        {
            type: 'text',
            name: 'last_name',
            label: 'billing.general.last_name_label',
            placeholder: 'billing.general.last_name_label',
            width: '50%',
            group: 'field-row',
        },
        {
            type: 'email',
            name: 'email',
            label: 'billing.general.correspondence_email_label',
            placeholder: 'billing.general.email_placeholder',
            helperText: 'billing.general.correspondence_email_helperText'
        },
        {
            type: 'text',
            name: 'invoice_email',
            label: 'billing.general.invoice_email_label',
            placeholder: 'billing.general.email_placeholder',
            helperText: 'billing.general.invoice_email_helperText'
        },
        {
            type: 'header',
            header: 'billing.general.invoice_address_header'
        },
        {
            type: 'text',
            name: 'street',
            label: 'billing.general.street_label',
            placeholder: 'billing.general.street_label',
            width: '70%',
            group: 'field-row',
        },
        {
            type: 'text',
            name: 'house_number',
            label: 'billing.general.house_number_label',
            placeholder: 'billing.general.house_number_label',
            width: '30%',
            group: 'field-row',
        },
        {
            type: 'text',
            name: 'postal_code',
            label: 'billing.general.postal_code_label',
            placeholder: 'billing.general.postal_code_label',
            width: '40%',
            group: 'field-row-next',
        },
        {
            type: 'text',
            name: 'city',
            label: 'billing.general.city_label',
            placeholder: 'billing.general.city_label',
            width: '60%',
            group: 'field-row-next',
        },
        {
            type: 'text',
            name: 'country',
            label: 'billing.general.country_label',
            placeholder: 'billing.general.country_label',
        },
        {
            type: 'text',
            name: 'vat_number',
            label: 'billing.general.vat_number_label',
            placeholder: 'billing.general.vat_number_placeholder',
        },
    ];
    
    // Reveal the modal on first render
    useEffect(() => {
        revealModal();
    }, []);

    // Generate the field component for the form
    const fieldComponents = useMemo(() => {
        if (!viewKey) return;
        return generateFieldComponents(
            viewKey, 
            fields, 
            undefined, 
            undefined, 
            billingData, 
            updatedData,
            activeFeatures
        );
    }, [updatedData, billingData]);

    // Refresh the payment elements
    useEffect(() => {
        if (elements) {
            billingDetailsRef.current = {
                getCardElement: () => elements.getElement(CardElement),
                getIbanElement: () => elements.getElement(IbanElement),
            };
        }
    }, [elements]);

    // Handle the submit of the updated billing details
    const handleSubmit = async () => {
        try {            
            // Start the button loader
            setButtonLoader(true);

            // Show error if sepa mandate is not accepted
            if (!sepaMandateAccepted) {
                setShowSepaWarning(true);

                setFloatingAlert({
                    type: "danger",
                    message: "billing.general.accept_mandate_required"
                });
                return;
            }

            // Combine the updated data and billing data
            const localBillingData: BillingData = {
                ...billingData,
                ...updatedData
            };

            // Initialize object to store the row errors
            const rowErrors: Record<string, string> = {};

            // Set the required billing data fields
            const requiredFields: (keyof BillingData)[] = [
                'company_name',
                'first_name',
                'last_name',
                'email',
                'street',
                'house_number',
                'city',
            ];

            // Validate the required fields
            for (const fieldName of requiredFields) {
                if (!localBillingData[fieldName]) {
                    rowErrors[fieldName] = 'validation.general.required';
                }
            }
            
            // Show the errors
            if (Object.keys(rowErrors).length > 0) {
                // Update the error messages state
                setErrorMessages(rowErrors);

                // Stop the button loader
                setButtonLoader(false);
            }

            // Check if the payment method is entered
            if (!isPaymentMethodEntered) {
                // Show payment method validation message
                setShowValidationMessage(true);

                // Show floating alert
                setFloatingAlert({ type: 'danger', message: 'billing.general.mandatory_fields_error' })
            }

            // Return out of the submit if the errors are shown
            if (Object.keys(rowErrors).length > 0 || !isPaymentMethodEntered) {
                return;
            }

            // Hide payment method validation
            setShowValidationMessage(false);

            // Get the current stripe customer id from the billing data
            let stripeCustomerId = localBillingData.stripe_customer_id;

            // If no stripe customer id exists, create it
            if (!stripeCustomerId) {

                // Create the stripe customer id
                stripeCustomerId = await createStripeCustomer(authContext.environmentHash, localBillingData)

                // Add the new customer id to the local billing data
                localBillingData.stripe_customer_id = stripeCustomerId;
            }

            // Create the sepa setup intent
            if (stripeCustomerId) {
                const setupIntent = await createSepaSetupIntent(
                    stripe,
                    elements,
                    stripeCustomerId,
                    authContext.environmentHash,
                    billingData,
                    paymentMethodData,
                    billingDetailsRef
                );

                // Handle successful response if the payment method is created
                if (setupIntent) {

                    // Update the provisional subscription
                    const response = await saveData({ 
                        apiUrl: `patch_provisional_subscription/${authContext.environmentHash}`, 
                        method: 'patch', 
                        data: {
                            subscription_id: subscriptionId,
                            billingData: localBillingData,
                        }
                    })

                    // Handle successful response
                    if (response?.status === 200) {

                        // Stop the button loader
                        setButtonLoader(false);

                        // Show success alert
                        setFloatingAlert({ 'type': 'success' })

                        // Update the authentication context to hide the no payment method banner
                        authContext.setSubscriptionNoSepaMandate(response.data.no_payment_method);
                        authContext.setSubscriptionIsProvisional(false);
                        localStorage.setItem("no_payment_method", encryptData(response.data.no_payment_method));
                        localStorage.setItem("is_provisional", encryptData(String(false)));
                        
                        // Call the on success callback
                        if (topModal?.props?.onSuccess) {
                            topModal.props.onSuccess();
                        };

                        // Close the modal
                        closeModal()

                        // Remove the error messages
                        setErrorMessages({});
                    }
                }
            }            
        } catch (error) {
            console.error("Error on submit", error);
            setButtonLoader(false);
        };        
    };

    // Close the modal
    const handleClose = () => {
        setButtonLoader(false);
        setErrorMessages({});
        closeModal();
    };

    return (
        <FormFieldContext.Provider value={{ editing, setEditing, updatedData, setUpdatedData }}>
            {(errorMessages.general) &&
                <div className="alert form-alert alert-danger" role="alert">
                    {t(errorMessages.general, { defaultValue: errorMessages.general })}
                </div>
            }
            <form className='formset add-edit-form payment-method-form'>
                {fieldComponents}
                <div className='formfield'>
                    <div className='field-header'>
                        <h6 className='header'>{t('billing.general.payment_method_header')}</h6>
                    </div>
                </div>
                <div className='formfield'>
                    <label>
                        {t('billing.general.payment_method_header')}
                        {showValidationMessage && !isPaymentMethodEntered && (
                            <span className='mandatory-error'>
                                {t('billing.general.mandatory_field')}
                            </span>
                        )}
                    </label>
                    <Dropdown<{ value: string, name: string }>
                        options={paymentMethods}
                        name='payment_method'
                        disabled_selected={t('billing.general.select_payment_method_disabled_selected')}
                        selectedOption={paymentMethods.find(method => method.value === selectedPaymentMethod)}
                        onChange={(selectedValue) => {
                            setSelectedPaymentMethod(selectedValue);
                            setPaymentMethodData({...paymentMethodData, payment_type: selectedValue as PaymentMethodType})
                        }}
                        selectionFormat={(option) => `${option.name}`}
                        optionFormat='name'
                        allowNoneOption={false} 
                        showSearch={false}
                    />
                </div>

                {selectedPaymentMethod === 'sepa_debit' && (
                    <div>
                        <div className='formfield'>
                            <label>{t('billing.general.iban_holder_name_label')}</label>
                            <input
                                type="text"
                                onChange={(e) => setPaymentMethodData({...paymentMethodData, ['sepa_account_holder']: e.target.value})}
                                placeholder={t('billing.general.iban_holder_name_label')}
                                className='text'
                            />
                        </div>
                        <div className='formfield'>
                            <label>{t('billing.general.iban_number_label')}</label>
                            <div className='sepa-field'>
                                <IbanElement
                                    options={{
                                        supportedCountries: ['SEPA'],
                                        style: {
                                            base: {
                                                fontSize: '1rem',
                                                fontFamily: 'Gotham-Book',
                                                color: '#1a1d27',
                                                '::placeholder': {
                                                    color: '#757575',
                                                },
                                            },
                                        },
                                    }}
                                    onChange={(e) => setIsPaymentMethodEntered(e.complete)}
                                />
                            </div>
                        </div>
                        <br />
                        <div className='mandate'>
                            <h6>
                                {t("billing.general.sepa_mandate_title")}
                            </h6>
                            <p>
                                {t("billing.general.sepa_mandate_description")}
                            </p>
                            <div className={showSepaWarning ? 'warning' : ''}>
                                <Checkbox
                                    name="sepa_mandate_accepted"
                                    label="billing.general.accept_mandate_label"
                                    align='left'
                                    value={sepaMandateAccepted}
                                    onChange={(e) => {
                                        if (e.target.checked === true) {
                                            setShowSepaWarning(false);
                                        }
                                        setSepaMandateAccepted(e.target.checked)
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}

                <div className='buttons-right'>
                    <SecondaryButton
                        onClick={handleClose} 
                        label='general.cancel'
                        size="small"
                    />
                    <PrimaryButton
                        type="button" 
                        label='general.save'
                        onClick={handleSubmit}
                        size="small"
                        onlyViewRestriction={true}
                        loading={buttonLoader}
                    />
                </div>
            </form>
        </FormFieldContext.Provider>
    )
};

export default AddPaymentDetailsForm;